import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Clock, Save, AlertTriangle, RefreshCw, 
  CheckCircle2, User, Edit, History
} from "lucide-react";
import CorrectionDialog from "./CorrectionDialog";
import HistoryDialog from "./HistoryDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AutoPunchOutTab = ({ selectedBusinessId, userRole, officeEndTime }) => {
  const [autoPunchOutSettings, setAutoPunchOutSettings] = useState({
    auto_punch_out_enabled: true,
    auto_punch_out_buffer_minutes: 120,
    auto_punch_out_action: "auto_punch_out",
    prevent_next_day_punch_without_correction: false
  });
  const [savingAutoPunchOut, setSavingAutoPunchOut] = useState(false);
  
  const [missingPunchOuts, setMissingPunchOuts] = useState([]);
  const [loadingMissingPunchOuts, setLoadingMissingPunchOuts] = useState(false);
  
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [recordHistory, setRecordHistory] = useState(null);

  const fetchAutoPunchOutSettings = useCallback(async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/settings/auto-punch-out?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/settings/auto-punch-out`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setAutoPunchOutSettings(data);
      }
    } catch (error) {
      console.error("Error fetching auto punch-out settings:", error);
    }
  }, [selectedBusinessId]);

  const fetchMissingPunchOuts = useCallback(async () => {
    try {
      setLoadingMissingPunchOuts(true);
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/missing-punch-outs?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/missing-punch-outs`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMissingPunchOuts(data.records || []);
      }
    } catch (error) {
      console.error("Error fetching missing punch-outs:", error);
    } finally {
      setLoadingMissingPunchOuts(false);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    fetchAutoPunchOutSettings();
    fetchMissingPunchOuts();
  }, [fetchAutoPunchOutSettings, fetchMissingPunchOuts]);

  const handleSaveAutoPunchOutSettings = async () => {
    setSavingAutoPunchOut(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/settings/auto-punch-out`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...autoPunchOutSettings,
          business_id: selectedBusinessId
        })
      });

      if (response.ok) {
        toast.success("Auto punch-out settings saved");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Error saving settings");
    } finally {
      setSavingAutoPunchOut(false);
    }
  };

  const handleOpenCorrection = (record) => {
    setSelectedRecord(record);
    setCorrectionDialogOpen(true);
  };

  const handleViewHistory = async (record) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/record-history/${record.attendance_id}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecordHistory(data);
        setHistoryDialogOpen(true);
      } else {
        toast.error("Failed to load history");
      }
    } catch (error) {
      toast.error("Error loading history");
    }
  };

  const handleTriggerAutoPunchOut = async () => {
    if (!confirm("This will process all missing punch-outs now. Continue?")) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/trigger-auto-punch-out`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Auto punch-out processing triggered");
        setTimeout(() => fetchMissingPunchOuts(), 3000);
      } else {
        toast.error("Failed to trigger processing");
      }
    } catch (error) {
      toast.error("Error triggering processing");
    }
  };

  return (
    <div className="space-y-6 mt-6">
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="w-5 h-5 text-amber-600" />
            Auto Punch-Out Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div>
              <Label className="text-base font-medium">Enable Auto Punch-Out</Label>
              <p className="text-sm text-slate-500 mt-1">
                Automatically mark punch-out for employees who forget
              </p>
            </div>
            <Switch
              checked={autoPunchOutSettings.auto_punch_out_enabled}
              onCheckedChange={(checked) => setAutoPunchOutSettings(prev => ({
                ...prev,
                auto_punch_out_enabled: checked
              }))}
              data-testid="auto-punch-out-toggle"
            />
          </div>

          {autoPunchOutSettings.auto_punch_out_enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Buffer Time (minutes)</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Wait time after office end before auto punch-out
                  </p>
                  <Select
                    value={autoPunchOutSettings.auto_punch_out_buffer_minutes?.toString()}
                    onValueChange={(v) => setAutoPunchOutSettings(prev => ({
                      ...prev,
                      auto_punch_out_buffer_minutes: parseInt(v)
                    }))}
                  >
                    <SelectTrigger data-testid="buffer-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="180">3 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium">Action Type</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    What to do when employee forgets punch-out
                  </p>
                  <Select
                    value={autoPunchOutSettings.auto_punch_out_action}
                    onValueChange={(v) => setAutoPunchOutSettings(prev => ({
                      ...prev,
                      auto_punch_out_action: v
                    }))}
                  >
                    <SelectTrigger data-testid="action-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto_punch_out">Auto punch-out at office end time</SelectItem>
                      <SelectItem value="half_day">Mark as Half Day</SelectItem>
                      <SelectItem value="pending">Keep Pending for Admin Approval</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div>
                  <Label className="text-base font-medium text-amber-700 dark:text-amber-300">
                    Block Next Day Punch-In
                  </Label>
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                    Prevent punch-in until previous missing punch-out is corrected
                  </p>
                </div>
                <Switch
                  checked={autoPunchOutSettings.prevent_next_day_punch_without_correction}
                  onCheckedChange={(checked) => setAutoPunchOutSettings(prev => ({
                    ...prev,
                    prevent_next_day_punch_without_correction: checked
                  }))}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            {userRole === "super_admin" && (
              <Button
                variant="outline"
                onClick={handleTriggerAutoPunchOut}
                className="mr-auto"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Process Now
              </Button>
            )}
            <Button
              onClick={handleSaveAutoPunchOutSettings}
              disabled={savingAutoPunchOut}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingAutoPunchOut ? "Saving..." : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Missing Punch-Outs
            {missingPunchOuts.length > 0 && (
              <Badge variant="destructive" className="ml-2">{missingPunchOuts.length}</Badge>
            )}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMissingPunchOuts}
            disabled={loadingMissingPunchOuts}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingMissingPunchOuts ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loadingMissingPunchOuts ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : missingPunchOuts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mb-3 text-emerald-500" />
              <p className="text-base font-medium">All Good!</p>
              <p className="text-sm">No missing punch-outs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Punch In</TableHead>
                    <TableHead className="font-semibold">Days Pending</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missingPunchOuts.map((record) => (
                    <TableRow key={record.attendance_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell className="font-medium">
                        {new Date(record.date).toLocaleDateString("en-IN", {
                          weekday: "short",
                          day: "2-digit",
                          month: "short"
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                            <User className="w-3 h-3 text-red-600" />
                          </div>
                          <span className="text-sm">{record.user_name || record.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-emerald-600 font-medium">
                        {record.punch_in_time_display || 
                          (record.punch_in_time ? new Date(record.punch_in_time).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit"
                          }) : "--:--")}
                      </TableCell>
                      <TableCell>
                        <Badge variant={record.days_pending > 1 ? "destructive" : "secondary"}>
                          {record.days_pending} day{record.days_pending !== 1 ? "s" : ""}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-red-600 border-red-300">
                          Missing P/O
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenCorrection(record)}
                            className="h-7 px-2 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                            data-testid={`correct-${record.attendance_id}`}
                          >
                            <Edit className="w-3.5 h-3.5 text-blue-600 mr-1" />
                            <span className="text-xs">Correct</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewHistory(record)}
                            className="h-7 px-2 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <History className="w-3.5 h-3.5 text-slate-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CorrectionDialog
        open={correctionDialogOpen}
        onOpenChange={setCorrectionDialogOpen}
        selectedRecord={selectedRecord}
        defaultTime={officeEndTime || "18:00"}
        onCorrectionComplete={fetchMissingPunchOuts}
      />

      <HistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        recordHistory={recordHistory}
      />
    </div>
  );
};

export default AutoPunchOutTab;
